/**
 * Access + refresh token storage abstraction.
 *
 * Configurable via `VITE_AUTH_STORAGE`:
 *   - localStorage   persists across tabs and reloads
 *   - sessionStorage clears on tab close
 *   - memory         never persisted (most secure, most friction)
 *
 * The HTTP client reads `getAccessToken()` to attach Authorization
 * headers. The auth service writes via `setTokens()` on sign-in /
 * refresh and clears via `clearTokens()` on sign-out.
 *
 * `expiresAt` (epoch seconds) lets the refresh interceptor decide
 * whether to proactively refresh before sending the next request.
 */

import config from './config'

const EXPIRES_KEY = 'lx.auth.expires'

const inMemory: {
  access: string | null
  refresh: string | null
  expiresAt: number | null
} = { access: null, refresh: null, expiresAt: null }

function storage(): Storage | null {
  if (typeof window === 'undefined') return null
  if (config.auth.storage === 'memory') return null
  if (config.auth.storage === 'sessionStorage') return window.sessionStorage
  return window.localStorage
}

function read(key: string): string | null {
  const s = storage()
  if (s) return s.getItem(key)
  if (key === config.auth.accessKey) return inMemory.access
  if (key === config.auth.refreshKey) return inMemory.refresh
  return null
}
function write(key: string, value: string | null): void {
  const s = storage()
  if (s) {
    if (value == null) s.removeItem(key)
    else s.setItem(key, value)
    return
  }
  if (key === config.auth.accessKey) inMemory.access = value
  if (key === config.auth.refreshKey) inMemory.refresh = value
  if (key === EXPIRES_KEY) inMemory.expiresAt = value == null ? null : Number(value)
}

export function getAccessToken(): string | null {
  return read(config.auth.accessKey)
}
export function getRefreshToken(): string | null {
  return read(config.auth.refreshKey)
}

export interface TokenSet {
  accessToken?: string | null
  refreshToken?: string | null
  expiresAt?: number | null
}

export function setTokens({ accessToken, refreshToken, expiresAt }: TokenSet = {}): void {
  if (accessToken !== undefined) write(config.auth.accessKey, accessToken)
  if (refreshToken !== undefined) write(config.auth.refreshKey, refreshToken)
  if (expiresAt !== undefined) {
    /* Persisted alongside the tokens, not just held in memory: a reload
     * restores the token from storage but used to lose its expiry, so
     * shouldRefresh() answered false forever and the *first* request of
     * every session went out with a possibly-dead token, relying on the
     * 401 path to rescue it. */
    inMemory.expiresAt = expiresAt ?? null
    write(EXPIRES_KEY, expiresAt == null ? null : String(expiresAt))
  }
}

export function getExpiresAt(): number | null {
  if (inMemory.expiresAt != null) return inMemory.expiresAt
  const raw = read(EXPIRES_KEY)
  const n = raw == null ? NaN : Number(raw)
  return Number.isFinite(n) ? n : null
}

export function clearTokens(): void {
  write(config.auth.accessKey, null)
  write(config.auth.refreshKey, null)
  write(EXPIRES_KEY, null)
  inMemory.expiresAt = null
}

/**
 * Whether the current access token is within `refreshLeeway` of
 * expiry. The refresh interceptor will short-circuit and grab a
 * new token before sending the request when this returns true.
 */
export function shouldRefresh(): boolean {
  const expiresAt = getExpiresAt()
  if (!expiresAt) return false
  const nowSec = Math.floor(Date.now() / 1000)
  return expiresAt - nowSec <= config.auth.refreshLeeway
}
